import React, { FormEvent, useEffect, useState } from 'react';
import { CommunicationUI } from '@/features/communication/screens/Communication/ui';
import useForm from '@/hooks/useForm';
import validators from '@/helpers/validators';
import { FormCommunication } from '@/features/communication/types';
import { OptionProps } from '@/components/Select';
import Event from '@/model/Event';
import { States } from '@/helpers/common/states';
import api from '@/services/api';
import { AxiosError } from 'axios';
import { toast } from 'react-toastify';
import CommunicationType from '@/model/CommunicationType';
import Communication from '@/model/Communication';
import CreateCommunication from '@/model/CreateCommunication';

interface IbgeMunicipioDTO {
  id: number;
  nome: string;
}

export const CommunicationScreen: React.FC = (): JSX.Element => {
  const [selectedChannels, setSelectedChannels] = useState<string[]>([]);
  const [events, setEvents] = useState<OptionProps[]>([]);
  const [state, setStatus] = useState<States>(States.default);
  const [type, setType] = useState<'TEMPLATE' | 'CUSTOM'>('TEMPLATE');
  const [cities, setCities] = useState<OptionProps[]>([]);
  const [selectedCities, setSelectedCities] = useState<OptionProps[]>([]);
  const [currentCityOption, setCurrentCityOption] = useState<OptionProps | null>(null);

  const {
    formData: formDataCommunication,
    formErrors: formErrorsCommunication,
    onChangeFormInput: onChangeFormInputCommunication,
    resetForm: resetFormCommunication,
  } = useForm({
    initialData: {
      name: '',
      chanel: '',
      city: '',
      country: '',
      radius: '',
      type_communication: 'TEMPLATE',
      template: '',
      messages: '',
      events: '',
      messageAmount: '',
    },
    validators: {
      name: [validators.required],
      country: [validators.required],
      city: [validators.required],
      radius: [validators.required],
      events: [validators.required],
      messageAmount: [validators.required],
    },
  });
  const handleOnGetEvents = async (): Promise<void> => {
    try {
      setStatus(States.loading);
      const { data } = await api.get<Event[]>(`/order/event`);
      if (data.length > 0) {
        const eventsOptions: OptionProps[] = [];
        data.forEach(dataEvents => {
          const newEventOptions: OptionProps = {
            value: dataEvents.id,
            label: dataEvents.name,
          };
          eventsOptions.push(newEventOptions);
        });
        setEvents(eventsOptions);
      }
    } catch (error) {
      const err = error as AxiosError;
      toast.error(err.message);
    } finally {
      setStatus(States.default);
    }
  };

  const handleOnToggleChanel = (channel: string): void => {
    setSelectedChannels(prev => {
      const next = prev.includes(channel) ? prev.filter(c => c !== channel) : [...prev, channel];
      // onChangeFormInputCommunication(FormCommunication.chanel)(next[0]);
      return next;
    });
  };

  const handleCommunicationType = (chanel: string): CommunicationType => {
    let newType: CommunicationType;
    switch (chanel) {
      case 'whatsapp':
        newType = CommunicationType.WHATSAPP;
        break;
      case 'gmail':
        newType = CommunicationType.EMAIL;
        break;
      case 'notification':
        newType = CommunicationType.PUSH_NOTIFICATION;
        break;
      default:
        newType = CommunicationType.WHATSAPP;
    }
    return newType;
  };

  const handleOnsubmit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    try {
      setStatus(States.loading);
      const types: CommunicationType[] = [];
      if (selectedChannels.length > 0) {
        // eslint-disable-next-line no-plusplus
        for (let i = 0; i < selectedChannels.length; i++) {
          const newTypes = handleCommunicationType(selectedChannels[i]);
          types.push(newTypes);
        }
      }
      if (types.length === 0) {
        toast.error('Informe ao menos um canal!');
        return;
      }
      if (selectedCities.length === 0) {
        toast.error('Informe ao menos uma cidade!');
        return;
      }
      if (!formDataCommunication[FormCommunication.events]) {
        toast.error('Informe um evento!');
        return;
      }
      const newCities: string[] = [];
      // eslint-disable-next-line no-plusplus
      for (let s = 0; s < selectedCities.length; s++) {
        newCities.push(selectedCities[s].label as string);
      }

      const payload = {
        name: formDataCommunication[FormCommunication.name],
        communicationChanel: types,
        state: formDataCommunication[FormCommunication.country],
        cities: newCities,
        customerLimit: Number(formDataCommunication[FormCommunication.messageAmount]),
        event: formDataCommunication[FormCommunication.events],
        template: formDataCommunication[FormCommunication.template]
          ? formDataCommunication[FormCommunication.template]
          : undefined,
        mensage: formDataCommunication[FormCommunication.messages]
          ? formDataCommunication[FormCommunication.messages]
          : undefined,
        startDate: undefined,
        endDate: undefined,
        radius: Number(formDataCommunication[FormCommunication.radius]),
      } as CreateCommunication;
      const response = await api.post<Communication>('/communication', payload);
      if (response.status === 201) {
        toast.success('Campanha Cadatrada com sucesso!');
        resetFormCommunication();
        setCities([]);
        setSelectedCities([]);
      }
    } catch (error) {
      const err = error as AxiosError;
      toast.error(err.message);
    } finally {
      setStatus(States.default);
    }
  };

  const getCitiesByUF = async (uf: string): Promise<OptionProps[]> => {
    const key = uf.trim().toUpperCase();

    if (!key) return [];
    const res = await fetch(
      `https://servicodados.ibge.gov.br/api/v1/localidades/estados/${key}/municipios`,
    );

    if (!res.ok) {
      throw new Error(`IBGE erro ao buscar cidades de ${key}`);
    }

    const data: IbgeMunicipioDTO[] = await res.json();

    const options: OptionProps[] = data
      .sort((a, b) => a.nome.localeCompare(b.nome))
      .map(c => ({
        value: c.nome,
        label: c.nome,
      }));

    setCities(options);
    return options;
  };

  const handleOntypeChange = (value: 'TEMPLATE' | 'CUSTOM'): void => {
    setType(value);
    onChangeFormInputCommunication(FormCommunication.type_communication)(value);

    if (value === 'TEMPLATE') {
      onChangeFormInputCommunication(FormCommunication.messages)('');
    } else {
      onChangeFormInputCommunication(FormCommunication.template)('');
    }
  };

  const handleAddCity = (): void => {
    if (!currentCityOption?.value) return;

    setSelectedCities(prev => {
      const exists = prev.some(c => c.value === currentCityOption.value);
      if (exists) return prev;
      return [...prev, currentCityOption];
    });

    onChangeFormInputCommunication(FormCommunication.city)('');
    setCurrentCityOption(null);
  };

  const handleRemoveCity = (value?: string | number): void => {
    setSelectedCities(prev => prev.filter(c => c.value !== value));
  };

  useEffect(() => {
    handleOnGetEvents();
  }, []);

  return (
    <CommunicationUI
      state={state}
      formData={formDataCommunication}
      formErrors={formErrorsCommunication}
      selectedChannels={selectedChannels}
      onChangeFormInput={onChangeFormInputCommunication}
      onSubmit={handleOnsubmit}
      events={events}
      type={type}
      toggleChannel={handleOnToggleChanel}
      onTypeChange={handleOntypeChange}
      cities={cities}
      getCities={getCitiesByUF}
      currentCityOption={currentCityOption}
      setCurrentCityOption={setCurrentCityOption}
      selectedCities={selectedCities}
      onAddCity={handleAddCity}
      onRemoveCity={handleRemoveCity}
    />
  );
};
